/**
 * The action bar: the loadout's nine action slots (each on a key the player picks). Each hotkey is
 * a quick link, not extra inventory: it points at a carried inventory slot
 * (and triggers whatever item occupies it) or at a skill. Each character
 * keeps its own mapping (see `useActionBarTargets`). See
 * docs/empyreal-crystal-mechanics.md.
 */
import type { ActionBarHotkeyTarget, EmpyrealCrystalSize, EmpyrealCrystalType } from '@sorrem/shared';
import { isBothHanded } from '@sorrem/shared';
import type { UIInventoryItem } from '@sorrem-client/types';
import { toHighlightedEquipmentSlotIndices } from './equipment-slots';

/** What pressing a hotkey does right now, given what occupies its carried slot. */
export type ActionBarActivation =
    | { readonly kind: 'use-crystal'; readonly itemId: string }
    | {
        readonly kind: 'equip';
        readonly itemId: string;
        readonly equipmentType: NonNullable<UIInventoryItem['equipmentType']>;
        /** Set for a weapon that takes both hands, which only the main hand can hold. */
        readonly takesBothHands?: true;
    }
    | { readonly kind: 'skill'; readonly skillId: string }
    | { readonly kind: 'none' };

/** What a hotkey shows: the item in its carried slot, a skill, an empty linked slot, or nothing. */
export type ActionBarSlotView =
    | { readonly kind: 'crystal'; readonly item: UIInventoryItem; readonly crystalType: EmpyrealCrystalType; readonly size: EmpyrealCrystalSize }
    | { readonly kind: 'item'; readonly item: UIInventoryItem }
    | { readonly kind: 'empty-slot'; readonly slotIndex: number }
    | { readonly kind: 'skill'; readonly skillId: string }
    | { readonly kind: 'unassigned' };

export function toActionBarSlotView(
    target: ActionBarHotkeyTarget | undefined,
    carriedItems: ReadonlyArray<UIInventoryItem | undefined>,
): ActionBarSlotView {
    if (!target) {
        return { kind: 'unassigned' };
    }
    if (target.kind === 'skill') {
        return { kind: 'skill', skillId: target.skillId };
    }

    const item = carriedItems[target.slotIndex];
    if (!item) {
        return { kind: 'empty-slot', slotIndex: target.slotIndex };
    }
    return item.crystal
        ? { kind: 'crystal', item, crystalType: item.crystal.crystalType, size: item.crystal.size }
        : { kind: 'item', item };
}

/**
 * Resolves a hotkey press: use one crystal from the stack in the linked
 * slot, equip the equipment there, or (for a skill) nothing yet.
 */
export function resolveActionBarActivation(
    target: ActionBarHotkeyTarget | undefined,
    carriedItems: ReadonlyArray<UIInventoryItem | undefined>,
): ActionBarActivation {
    const view = toActionBarSlotView(target, carriedItems);
    if (view.kind === 'crystal') {
        return { kind: 'use-crystal', itemId: view.item.id };
    }
    if (view.kind === 'item' && view.item.equipmentType) {
        return {
            kind: 'equip',
            itemId: view.item.id,
            equipmentType: view.item.equipmentType,
            ...(isBothHanded(view.item) ? { takesBothHands: true } : {}),
        };
    }
    if (view.kind === 'skill') {
        return { kind: 'skill', skillId: view.skillId };
    }
    return { kind: 'none' };
}

/**
 * The loadout slot (7-slot order) an item of `equipmentType` is equipped
 * into from a hotkey: the first empty valid slot, else the first valid slot
 * (swapping out what's there). -1 when the type can't be equipped.
 */
export function resolveHotkeyEquipSlotIndex(
    equipmentType: UIInventoryItem['equipmentType'],
    equipmentSlots: ReadonlyArray<{ readonly id: string } | null | undefined>,
    takesBothHands = false,
): number {
    const validIndices = toHighlightedEquipmentSlotIndices(equipmentType, takesBothHands);
    return validIndices.find((index) => !equipmentSlots[index]) ?? validIndices[0] ?? -1;
}

/**
 * Links hotkey `hotkeyIndex` to carried slot `slotIndex`, or unlinks it if
 * it already points there. Returns a new mapping.
 */
export function withCarriedSlotHotkeyToggled(
    hotkeys: readonly ActionBarHotkeyTarget[],
    hotkeyIndex: number,
    slotIndex: number,
): ActionBarHotkeyTarget[] {
    const next = [...hotkeys];
    const current = next[hotkeyIndex];
    next[hotkeyIndex] = current?.kind === 'carried-slot' && current.slotIndex === slotIndex
        ? null
        : { kind: 'carried-slot', slotIndex };
    return next;
}

/**
 * Sets hotkey `hotkeyIndex` as carried slot `slotIndex`'s one hotkey: any
 * other hotkey linked to that slot is unlinked. Pressing the slot's current
 * hotkey again unlinks it. Returns a new mapping.
 */
export function withCarriedSlotHotkey(
    hotkeys: readonly ActionBarHotkeyTarget[],
    hotkeyIndex: number,
    slotIndex: number,
): ActionBarHotkeyTarget[] {
    const current = hotkeys[hotkeyIndex];
    const isAlreadyLinked = current?.kind === 'carried-slot' && current.slotIndex === slotIndex;
    return hotkeys.map((target, index) => {
        if (index === hotkeyIndex) {
            return isAlreadyLinked ? null : { kind: 'carried-slot', slotIndex };
        }
        return target?.kind === 'carried-slot' && target.slotIndex === slotIndex ? null : target;
    });
}

/** Every hotkey linked to carried slot `slotIndex`, by index, in order. */
export function listCarriedSlotHotkeyIndexes(hotkeys: readonly ActionBarHotkeyTarget[], slotIndex: number): number[] {
    return hotkeys.flatMap((target, index) => (target?.kind === 'carried-slot' && target.slotIndex === slotIndex ? [index] : []));
}

/** The first hotkey linked to carried slot `slotIndex`, or -1 if none is. */
export function findCarriedSlotHotkeyIndex(hotkeys: readonly ActionBarHotkeyTarget[], slotIndex: number): number {
    return hotkeys.findIndex((target) => target?.kind === 'carried-slot' && target.slotIndex === slotIndex);
}

/**
 * Puts skill `skillId` on hotkey `hotkeyIndex`, replacing whatever was
 * there. A skill has one hotkey, so any other hotkey it was on is cleared
 * (assigning a new key moves it). Returns a new mapping.
 */
export function withSkillHotkey(
    hotkeys: readonly ActionBarHotkeyTarget[],
    hotkeyIndex: number,
    skillId: string,
): ActionBarHotkeyTarget[] {
    return hotkeys.map((target, index) => {
        if (index === hotkeyIndex) {
            return { kind: 'skill', skillId };
        }
        return target?.kind === 'skill' && target.skillId === skillId ? null : target;
    });
}

/** Takes skill `skillId` off every hotkey it is on. Returns a new mapping. */
export function withoutSkillHotkey(hotkeys: readonly ActionBarHotkeyTarget[], skillId: string): ActionBarHotkeyTarget[] {
    return hotkeys.map((target) => (target?.kind === 'skill' && target.skillId === skillId ? null : target));
}

/** Empties hotkey `hotkeyIndex`, whatever it held. Returns a new mapping. */
export function withoutHotkey(hotkeys: readonly ActionBarHotkeyTarget[], hotkeyIndex: number): ActionBarHotkeyTarget[] {
    return hotkeys.map((target, index) => (index === hotkeyIndex ? null : target));
}

/** The first hotkey skill `skillId` is on, or -1 if it is on none. */
export function findSkillHotkeyIndex(hotkeys: readonly ActionBarHotkeyTarget[], skillId: string): number {
    return hotkeys.findIndex((target) => target?.kind === 'skill' && target.skillId === skillId);
}
